import { useEffect, useRef, useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { HISTORY_FIRST_PAGE_SIZE, HISTORY_PAGE_SIZE, historyApi, type HistoryChange, type HistoryEntityKind, type HistoryEntry } from "../api/history";
import type { ApiError } from "../api/client";
import { formatDateTime, formatRelativeSr } from "../lib/dates";
import { describeEntry, fieldLabel, formatValue } from "../lib/historyText";
import { useAuth } from "../auth/AuthContext";

/**
 * Who changed what and when on an entity (sprint 5d), read from the audit
 * log: newest first, exact stamp with the relative time in `title`, one
 * Serbian sentence per entry and a field-by-field "before → after" list
 * below it. "Prikaži starije" appends the next page.
 */
export function HistoryPanel({
  entityKind, entityId, title, refreshKey,
}: {
  entityKind: HistoryEntityKind;
  entityId: string;
  /** Card heading; defaults to "Istorija". */
  title?: string;
  /**
   * Change it after a write on the same screen: the newest entries are fetched and put on top
   * of what is already shown — nothing is cleared, the list does not flash.
   */
  refreshKey?: number;
}) {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const canView = hasPermission("history:view");

  const [entries, setEntries] = useState<HistoryEntry[] | null>(null);
  const [total, setTotal] = useState(0);
  // Full (20-row) pages already appended after the short first load.
  const [fullPages, setFullPages] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!canView) return;
    let cancelled = false;
    setEntries(null);
    setError(null);
    setFullPages(0);
    historyApi.list(entityKind, entityId, 0, HISTORY_FIRST_PAGE_SIZE)
      .then((res) => {
        if (cancelled) return;
        setEntries(res.content);
        setTotal(res.totalElements);
      })
      .catch((err: ApiError) => { if (!cancelled) { setError(err.message); setEntries([]); } });
    return () => { cancelled = true; };
  }, [entityKind, entityId, canView]);

  // After a save on the same screen: fetch the head of the list again and prepend what is new.
  const refreshed = useRef(refreshKey);
  useEffect(() => {
    if (refreshed.current === refreshKey) return;
    refreshed.current = refreshKey;
    if (!canView) return;
    let cancelled = false;
    historyApi.list(entityKind, entityId, 0, HISTORY_FIRST_PAGE_SIZE)
      .then((res) => {
        if (cancelled) return;
        setEntries((prev) => {
          const seen = new Set((prev ?? []).map((e) => e.id));
          return [...res.content.filter((e) => !seen.has(e.id)), ...(prev ?? [])];
        });
        setTotal(res.totalElements);
      })
      .catch((err: ApiError) => { if (!cancelled) setError(err.message); });
    return () => { cancelled = true; };
  }, [refreshKey, entityKind, entityId, canView]);

  // The first load is 5 rows, later loads are 20-row pages from offset 0, so the rows already on
  // screen are skipped by id instead of by offset arithmetic (new rows may also appear at the top).
  async function loadMore() {
    setLoadingMore(true);
    try {
      const res = await historyApi.list(entityKind, entityId, fullPages, HISTORY_PAGE_SIZE);
      setFullPages(fullPages + 1);
      setEntries((prev) => {
        const seen = new Set((prev ?? []).map((e) => e.id));
        return [...(prev ?? []), ...res.content.filter((e) => !seen.has(e.id))];
      });
      setTotal(res.totalElements);
    } catch (err) {
      setError((err as ApiError).message);
    } finally {
      setLoadingMore(false);
    }
  }

  const hasOlder = entries !== null && entries.length < total;

  // Without the view function the whole card stays out — the page does not need to know (audit 2026-09-23 §C).
  if (!canView) return null;

  return (
    <section className="card">
      <div className="card-h">
        <div>
          <h3>{title ?? t("history.title", "Istorija")}</h3>
          <div className="sub">
            {entries === null ? t("common.loading", "Učitavanje…") : t("history.count", "{{count}} izmena", { count: total })}
          </div>
        </div>
      </div>

      {error ? (
        <div style={{ ...errorBox, margin: 18 }} role="alert">{error}</div>
      ) : entries === null ? (
        <div className="empty">{t("common.loading", "Učitavanje…")}</div>
      ) : entries.length === 0 ? (
        <div className="empty">{t("history.empty", "Još nema izmena.")}</div>
      ) : (
        <div className="card-b">
          <ol className="hist-list">
            {entries.map((e) => <HistoryRow key={e.id} entry={e} />)}
          </ol>
          {hasOlder && (
            <div className="form-actions" style={{ justifyContent: "center", marginTop: 12 }}>
              <button type="button" className="btn sm" disabled={loadingMore} onClick={loadMore}>
                {loadingMore ? t("common.loading", "Učitavanje…") : t("history.loadOlder", "Prikaži starije")}
              </button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function HistoryRow({ entry }: { entry: HistoryEntry }) {
  const { t } = useTranslation();
  return (
    <li className={`hist-row hist-${entry.action.toLowerCase()}`}>
      <div className="hist-meta">
        <time className="hist-when" dateTime={entry.at} title={formatRelativeSr(entry.at)}>{formatDateTime(entry.at)}</time>
        <span className="hist-who">{entry.actorName ?? t("history.system", "sistem")}</span>
      </div>
      <div className="hist-what">{describeEntry(entry, t)}</div>
      {entry.changes.length > 0 && (
        <ul className="hist-changes">
          {/* CREATE and DELETE rows list values, not transitions (a delete carries only its reason). */}
          {entry.changes.map((c) => <ChangeRow key={c.field} change={c} showBefore={entry.action === "UPDATE"} />)}
        </ul>
      )}
    </li>
  );
}

function ChangeRow({ change, showBefore }: { change: HistoryChange; showBefore: boolean }) {
  const { t } = useTranslation();
  return (
    <li>
      <span className="hist-field">{fieldLabel(change.field, t)}:</span>
      {showBefore ? (
        <span className="from-to">
          <span className="hist-val">{formatValue(change.field, change.before, t)}</span>
          <span className="arrow" aria-hidden="true">→</span>
          <span className="hist-val">{formatValue(change.field, change.after, t)}</span>
        </span>
      ) : (
        <span className="hist-val">{formatValue(change.field, change.after, t)}</span>
      )}
    </li>
  );
}

const errorBox: CSSProperties = { padding: 10, background: "rgba(182,53,58,.08)", color: "var(--err)", borderRadius: 6, fontSize: 13 };
